import { useState } from "react";
import { useHistory } from "react-router-dom";

import { movieAssetsListRoute } from "../../../assets/AssetsRoutes";
import { MovieContentSubtype } from "../../../common/types/MovieContentSubtype";
import { getMovieDashboardHref } from "../../ContentRoutes";
import { GetMovieListRequest } from "../api/GetMovieListRequest";
import { useAssetsManageMovieApi } from "../api/useAssetsManageMovieApi";
import { mapDocumentToMovieItem, MovieItem } from "./MovieItem";
import { MovieListingFilters } from "./useMovieListingFilters";

export const useMovieListing = () => {
    const { getMovieList } = useAssetsManageMovieApi();

    const [items, setItems] = useState<MovieItem[]>([]);
    const [moviesToMerge, setMoviesToMerge] = useState<MovieItem[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [isListLoading, setIsListLoading] = useState<boolean>(true);
    const history = useHistory();

    const getData = async (filters: MovieListingFilters, movieContentType: MovieContentSubtype): Promise<void> => {
        setIsListLoading(true);

        const query: GetMovieListRequest = {
            subtype: movieContentType,
            page: filters.page,
            pageSize: filters.pageSize,
            sortBy: filters.sortBy,
            sortOrder: filters.sortOrder,
            title: filters.title,
            sources: filters.sources,
            includeMerged: filters.includeMerged
        };

        const response = await getMovieList(query);
        const movieManagerItems = response.items.map((item) => mapDocumentToMovieItem(item));

        setItems(movieManagerItems);
        setTotalItems(response.totalCount);
        setIsListLoading(false);
    };

    const getDashboardHref = (item: MovieItem, contentType: MovieContentSubtype) =>
        getMovieDashboardHref(item.id, contentType);

    const moveToMovieAssets = (movieId: string) => {
        const basePath = movieAssetsListRoute.path;
        const requiredPath = basePath?.slice(0, basePath.lastIndexOf("/"));
        history.push(`${requiredPath}/${movieId}`);
    };

    const removeMovieFromMergeList = (movieToRemove: MovieItem) => {
        setMoviesToMerge((prevMovies: MovieItem[]) => {
            return prevMovies.filter((movie: MovieItem) => movieToRemove.id !== movie.id);
        });
    };

    const updateListAfterMerge = (moviesToRemove: MovieItem[], newItem: MovieItem) => {
        setItems((prevItems: MovieItem[]) => {
            const filteredItems = prevItems.filter(
                (movieItem: MovieItem) =>
                    !moviesToRemove.some((movieToRemove: MovieItem) => movieToRemove.id === movieItem.id)
            );

            return [newItem, ...filteredItems];
        });
    };

    const addMovieItemToMergeList = (item: MovieItem) => {
        setMoviesToMerge((prevMovies: MovieItem[]) => {
            return [...prevMovies, item];
        });
    };

    const clearMoviesToMerge = () => {
        setMoviesToMerge([]);
    };

    return {
        items,
        totalItems,
        isListLoading,
        moviesToMerge,
        getData,
        getDashboardHref,
        setItems,
        moveToMovieAssets,
        removeMovieFromMergeList,
        updateListAfterMerge,
        clearMoviesToMerge,
        addMovieItemToMergeList
    };
};
